import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactI18n from 'react-i18n';
import {
  Box,
  DisplayLineItem,
  FlexBox,
  IconButton,
  IconFontAwesome,
  Spacer,
  TextDense,
  TextBody
} from '@directsoftware/ui-kit-web-admin';
import moment from 'moment/moment';
import { formatCurrency } from '../../../shared/helpers';
import {
  balanceDueIsInFuture,
  formatPaymentDate,
  guestBalance,
  paymentMethodDescription
} from '../../utils/payment-terms';
import {
  chargeRefundStatusLabel,
  refundTotal,
  settledGuestCharges
} from '../../utils/guest-charges';

const discountRateLabel = discount => {
  return discount.rate > 0 ? ` (${discount.rate}%)` : '';
};

// Promo names come through as internal codes (e.g. `SUMMER25`), so they are
// prefixed with the word discount unless they already read as one.
const discountLabel = (discount, translate) => {
  const name = String(discount.name || '').trim();
  const rate = discountRateLabel(discount);
  const discountWord = translate(`cx.receipt.discount`);
  if (!name) return `${discountWord}${rate}`;
  if (name.toLowerCase().includes('discount')) return `${name}${rate}`;
  return `${discountWord}: ${name}${rate}`;
};

const RenderDiscount = (discount, currency, key, translate) => {
  if (Number(discount.total_cents || 0) <= 0) {
    return null;
  }
  return (
    <DisplayLineItem
      key={key}
      label={discountLabel(discount, translate)}
      value={`- ${formatCurrency(
        Number(discount.total_cents || 0) / 100,
        currency
      )}`}
      labelIsRegularWeight
      collapseOnMobile={false}
      removeBottomBorder
      padding="xxs"
    />
  );
};

const RenderIncludedFeeLineItem = (fee, currency, key, translate) => {
  if (Number(fee.total_cents || 0) <= 0) {
    return null;
  }
  return (
    <DisplayLineItem
      key={key}
      label={
        <TextDense textColor="light-gray">
          {`${fee.name} ${translate(`cx.global.included_in_rate`)}`}
        </TextDense>
      }
      value={
        <TextDense textColor="light-gray">
          {formatCurrency(Number(fee.total_cents || 0) / 100, currency)}
        </TextDense>
      }
      labelIsRegularWeight
      collapseOnMobile={false}
      removeBottomBorder
      padding="xxs"
    />
  );
};

const RenderFeeLineItem = (fee, translate, currency) => {
  if (fee.additional_data?.included_in_base_rent) {
    return null;
  } else {
    return (
      <DisplayLineItem
        label={fee.name}
        value={formatCurrency(fee.total_cents / 100, currency)}
        labelIsRegularWeight
        collapseOnMobile={false}
        removeBottomBorder
        padding="xxs"
      />
    );
  }
};

const RenderTaxLineItem = (tax, currency, key) => (
  <DisplayLineItem
    key={key}
    label={tax.name || 'Taxes'}
    value={formatCurrency(Number(tax.total_cents || 0) / 100, currency)}
    labelIsRegularWeight
    collapseOnMobile={false}
    removeBottomBorder
    padding="xxs"
  />
);

const RenderLineItems = (pricing, nights, currency, translate) => {
  const fees = Array.isArray(pricing.fees) ? pricing.fees : [];

  return (
    <>
      <Box paddingLeft="s" className="feesTable">
        {fees.map(
          fee =>
            fee.total_cents > 0 && RenderFeeLineItem(fee, translate, currency)
        )}
      </Box>
      <Spacer size="xs" />
    </>
  );
};

const RenderCharge = (charge, key, translate, currency, displayFormat) => {
  const amount_charged = parseFloat(charge.amount_charged).toFixed(2);
  const description = [
    paymentMethodDescription(charge, translate),
    chargeRefundStatusLabel(charge, translate)
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <DisplayLineItem
      key={key}
      label={moment(charge.created_at).format(
        displayFormat === 'DD/MM/YYYY' ? 'D MMMM, YYYY' : 'MMMM D, YYYY'
      )}
      description={description}
      value={`- ${formatCurrency(amount_charged, currency)}`}
      valueClassName="success-green-text"
      labelIsRegularWeight
      collapseOnMobile={false}
      removeBottomBorder
      padding="xxs"
    />
  );
};

// A refund returns money to the guest, so it adds back to what is owed. The
// refunds are shown as one line, like the verification page does.
const RenderRefunded = (refunded, translate, currency) => (
  <DisplayLineItem
    label={translate(`cx.receipt.refunded`)}
    value={`+ ${formatCurrency(refunded, currency)}`}
    labelIsRegularWeight
    collapseOnMobile={false}
    removeBottomBorder
    padding="xxs"
  />
);

const PaymentBreakdown = ({ stateObject }) => {
  const brand = useSelector(state => state.brand);
  const translate = ReactI18n.getIntlMessage;
  const [openFees, setOpenFees] = useState(false);
  const [totalFees, setTotalFees] = useState(0);

  const { total, paid } = guestBalance(stateObject.booking);
  const discounts = Array.isArray(stateObject.pricing?.discount)
    ? stateObject.pricing.discount
    : [];
  const discountTotalCents = discounts.reduce((totalCents, discount) => {
    return totalCents + Number(discount.total_cents || 0);
  }, 0);
  const includedFees = Array.isArray(stateObject.pricing?.fees)
    ? stateObject.pricing.fees.filter(
        fee => fee.additional_data?.included_in_base_rent
      )
    : [];
  const taxLineItems = Array.isArray(stateObject.pricing?.tax_line_items)
    ? stateObject.pricing.tax_line_items
    : [];
  const settledCharges = settledGuestCharges(stateObject.charges);
  // The receipt endpoint lists the refunds; the payment endpoint may not, so
  // the refunded total falls back to what the settled charges exceed
  // price_paid by (see guest-charges.js).
  const refunded = refundTotal({
    charges: settledCharges,
    refunds: stateObject.refunds,
    pricePaid: paid
  });
  // Money scheduled for a later date is labelled with that date rather than as
  // due today.
  const scheduledDueDate = balanceDueIsInFuture(
    stateObject.payment_balance_due_on
  )
    ? formatPaymentDate(stateObject.payment_balance_due_on, brand.date_format)
    : '';
  const amountDueLabel = scheduledDueDate
    ? translate(`cx.global.scheduled_payment`, { date: scheduledDueDate })
    : translate(`cx.global.immediate_payment`);

  useEffect(() => {
    let feeTotal = 0;
    if (stateObject.pricing && Array.isArray(stateObject.pricing.fees)) {
      stateObject.pricing.fees.forEach(fee => {
        if (!fee.additional_data?.included_in_base_rent)
          feeTotal += fee.total_cents;
      });
    }
    if (feeTotal > 0) {
      setTotalFees(feeTotal / 100);
    }
  }, []);

  return (
    <>
      <TextBody textColor="dark-gray" weight="semibold">
        {translate(`cx.global.payment_info`)}
      </TextBody>
      <Spacer size="xs" />
      {stateObject.pricing && (
        <>
          <DisplayLineItem
            label={`${formatCurrency(
              stateObject.pricing.average_nightly_price,
              brand.currency
            )} * ${translate(
              `global.parsers.num_nights.${
                stateObject.nights > 1 ? 'plural' : 'single'
              }`,
              { nights: stateObject.nights }
            )}`}
            value={formatCurrency(
              stateObject.pricing.room_rate,
              brand.currency
            )}
            labelIsRegularWeight
            collapseOnMobile={false}
            removeBottomBorder
            padding="xxs"
          />
          {includedFees.map((fee, index) =>
            RenderIncludedFeeLineItem(
              fee,
              brand.currency,
              `included-${index}`,
              translate
            )
          )}
          {discounts.map((discount, index) =>
            RenderDiscount(
              discount,
              brand.currency,
              `discount-${index}`,
              translate
            )
          )}
          {taxLineItems.length > 0
            ? taxLineItems.map((tax, index) =>
                RenderTaxLineItem(tax, brand.currency, `tax-${index}`)
              )
            : stateObject.pricing.taxes > 0 && (
                <DisplayLineItem
                  label={translate(`cx.global.taxes`)}
                  value={formatCurrency(
                    stateObject.pricing.taxes,
                    brand.currency
                  )}
                  labelIsRegularWeight
                  collapseOnMobile={false}
                  removeBottomBorder
                  padding="xxs"
                />
              )}
          {totalFees > 0 && (
            <DisplayLineItem
              label={
                <FlexBox alignItems="center" gap="xs">
                  <Box>Fees</Box>
                  <IconButton
                    onClick={() => setOpenFees(!openFees)}
                    size="dense"
                    appearance="ghost"
                    className="feesTable__button"
                  >
                    <IconFontAwesome
                      name={openFees ? 'chevronUp' : 'chevronDown'}
                    />
                  </IconButton>
                </FlexBox>
              }
              value={formatCurrency(totalFees, brand.currency)}
              collapseOnMobile={false}
              padding="xxs"
              labelIsRegularWeight
              removeBottomBorder
            />
          )}
          {openFees &&
            RenderLineItems(
              stateObject.pricing,
              stateObject.nights,
              brand.currency,
              translate
            )}
          <Spacer size="xxs" />
        </>
      )}
      <FlexBox justifyContent="space-between">
        <TextBody textColor="dark-gray" weight="semibold">
          {translate(`cx.global.total`)}
        </TextBody>
        <TextBody textColor="dark-gray" weight="semibold">
          {formatCurrency(total, brand.currency)}
        </TextBody>
      </FlexBox>
      {discountTotalCents > 0 && (
        <DisplayLineItem
          label={translate(`cx.global.you_saved`)}
          value={formatCurrency(discountTotalCents / 100, brand.currency)}
          valueClassName="discount"
          labelIsRegularWeight
          collapseOnMobile={false}
          removeBottomBorder
          padding="xxs"
        />
      )}
      {Number(stateObject.payment_due_today || 0) > 0 && (
        <DisplayLineItem
          label={amountDueLabel}
          description={
            stateObject.payment_auto_collected
              ? translate(`cx.receipt.auto_collected`)
              : undefined
          }
          value={formatCurrency(
            Number(stateObject.payment_due_today),
            brand.currency
          )}
          labelIsRegularWeight
          collapseOnMobile={false}
          removeBottomBorder
          padding="xxs"
        />
      )}
      {settledCharges.length > 0 && (
        <>
          <Spacer size="l" />
          <TextBody textColor="dark-gray" weight="semibold">
            Charges
          </TextBody>
          <Spacer size="xs" />
          {settledCharges.map((charge, index) =>
            RenderCharge(
              charge,
              `charge-${charge.id || index}`,
              translate,
              brand.currency,
              brand.date_format
            )
          )}
        </>
      )}
      {refunded > 0 && (
        <>
          <Spacer size="l" />
          <TextBody textColor="dark-gray" weight="semibold">
            Refunds
          </TextBody>
          <Spacer size="xs" />
          {RenderRefunded(refunded, translate, brand.currency)}
        </>
      )}
      {(settledCharges.length > 0 || refunded > 0) && (
        <>
          <Spacer size="xs" />
          <DisplayLineItem
            label={translate(`cx.global.total_paid`)}
            value={formatCurrency(paid, brand.currency)}
            collapseOnMobile={false}
            removeBottomBorder
            padding="xxs"
          />
        </>
      )}
    </>
  );
};

export default PaymentBreakdown;
